import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { auth, db } from "firebase.js"; // Ensure correct Firebase import
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import * as Sentry from "@sentry/react";

// Create AuthContext
const AuthContext = createContext();

// The app's view of the signed-in user, or null when they may not use the app yet:
// no profile, or an email that is neither confirmed nor verified by a master.
const loadUser = async (currentUser) => {
  if (!currentUser) return null;

  const userDoc = await getDoc(doc(db, "users", currentUser.uid));
  if (!userDoc.exists()) return null;

  const data = userDoc.data();
  if (!currentUser.emailVerified && data.verified !== true) return null;

  return {
    uid: currentUser.uid,
    email: currentUser.email,
    name: data.name || "Utilisateur",
    role: data.role || "user",
    master: data.master || false,
    adminLocations: data.adminLocations || [],
    photoURL: currentUser.photoURL || null,
  };
};

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // For initial auth state loading
  // Loads can overlap (sign-in, then refreshUser); only the latest one may win
  const latestLoad = useRef(0);

  const load = useCallback(async (currentUser) => {
    const id = ++latestLoad.current;
    let next = null;
    try {
      next = await loadUser(currentUser);
    } catch (error) {
      Sentry.captureException(error);
    }
    if (id === latestLoad.current) {
      setUser(next);
      setLoading(false); // Auth state is now fully loaded
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, load);
    return () => unsubscribe(); // Cleanup listener on unmount
  }, [load]);

  // Re-read the profile, e.g. after login repaired or created it
  const refreshUser = useCallback(() => load(auth.currentUser), [load]);

  // Logout function
  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};
